import Button from '@ui/buttons/Button'
import { getConvertedPrice, shouldShowMonthly } from '@ca/common/types'
import SummaryItems from '@order/Summary/SummaryItems'
import DetailRow from '@ui/elements/DetailRow'
import PriceInfo from '@/price/PriceInfo'
import PriceSummary from '@order/PriceSummary'
import CommentModal from '@order/modals/Checkout/Comment/CommentModal'
import useDeal from '@order/hooks/useDeal'
import useDealSettings from '@order/hooks/useDealSettings'
import { useOrderState } from '@order/hooks/useOrderState'
import useOrderTotal from '@order/hooks/useOrderTotal'
import { useTotalDiscountAmount } from '@order/useDiscounts'
import { getFlow } from '@order/utils/getFlow'
import { usePriceFormatter } from 'hooks/useCurrency'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, { resolveDealsContext } from 'hooks/useSession'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import DiscountDetailsRow from '../discounts/DiscountDetailsRow'
import { useOrderSummary } from '@order/hooks/useOrderSummary'

type ModalName = 'stock' | 'comment'

interface Props {
  isLoading: boolean
  isCompleted: boolean
}

export default function OrderSummaryBody({ isLoading, isCompleted }: Props) {
  const { t } = useTranslation(['summary', 'steps'])
  const formatter = usePriceFormatter()
  const [modal, setModal] = useState<ModalName | null>(null)

  const openCommentModal = () => setModal('comment')
  const resetModal = () => setModal(null)

  const discounts = useOrderState((s) => s.discounts)
  const order = useOrderState((s) => s.order)
  const total = useOrderTotal()
  const discountAmount = useTotalDiscountAmount({
    orderTotal: total,
    discounts,
  })
  const comment = useOrderState((s) => s.order.comment)
  const summary = useOrderSummary()
  const result = useOrderState((s) => s.result)
  const frames = useOrderState((s) => s.order.items)
  const deals = useSession((s) => s.deals)
  const isAutoDiscount =
    !isLoading &&
    result?.isValid &&
    discountAmount.auto != null &&
    discountAmount.auto != 0

  const settings = useDealSettings()
  const { priceSuffix } = settings
  const showMonthly = shouldShowMonthly(settings)

  // This price is the total price with the installments applied
  const resolvedTotal = discountAmount.discounted

  const resolvedDeals = resolveDealsContext(deals)
  const config = useRemoteConfig()

  const baseDeal = useDeal(resolvedDeals.base?.dealId)

  const flow = getFlow(config, baseDeal?.settings?.flow)

  return (
    <>
      <View className="px-8">
        <View className="gap-0 overflow-hidden rounded-2xl border border-text/10">
          <SummaryItems
            expanded
            summary={summary}
            order={order}
            result={result}
            deals={resolvedDeals}
            frames={frames}
          />
          {discountAmount.hasDiscounts && (
            <DiscountDetailsRow
              deals={resolvedDeals}
              discounts={discounts}
              order={order}
              result={result}
              settings={settings}
            />
          )}
          {isAutoDiscount && isCompleted && (
            <DetailRow
              success
              title={t('summary:label.discountsAuto')}
              icon="gift"
            >
              <PriceInfo
                suffix={showMonthly ? settings.priceSuffix : undefined}
                value={
                  showMonthly && discountAmount.auto
                    ? getConvertedPrice(discountAmount.auto, settings)
                    : discountAmount.auto
                }
                size="default"
              />
            </DetailRow>
          )}
          {!flow.isSelfService && (
            <DetailRow
              icon="message-circle-2"
              title={t('modals:comment.title')}
              onPress={openCommentModal}
              success={!!comment}
            >
              <Button
                size="xs"
                variant="outlined"
                onPress={openCommentModal}
                label={t(comment ? 'ui:view' : 'ui:add')}
              />
            </DetailRow>
          )}
        </View>
      </View>
      <PriceSummary
        isLoading={isLoading}
        template="current"
        price={discountAmount.discountedDisplay}
        installment={total.showMonthly ? priceSuffix : undefined}
        textBottom={
          total.showMonthly
            ? t('summary:price.installments.total', {
                price: formatter.format(resolvedTotal),
                installments: total.installmentCount,
              })
            : undefined
        }
      />
      <CommentModal close={resetModal} open={modal === 'comment'} />
    </>
  )
}
